import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import apiServices from "../../services/apiServices";

// Async Thunks

// Create a new stock entry
export const createStock = createAsyncThunk(
  "stocks/createStock",
  async (stockData, { rejectWithValue }) => {
    try {
      const response = await apiServices.post(`stock`, stockData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get all stocks for an item
export const getStockByItem = createAsyncThunk(
  "stocks/getStockByItem",
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`stock/item/${itemId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Update stock after sale
export const updateStockOnSale = createAsyncThunk(
  "stocks/updateStockOnSale",
  async (saleData, { rejectWithValue }) => {
    try {
      const response = await apiServices.put(`stock/sale`, saleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get total current stock value
export const getCurrentStockValue = createAsyncThunk(
  "stocks/getCurrentStockValue",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`stock/current-stock-value`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get stock by batch number for a specific item
export const getStockByBatch = createAsyncThunk(
  "stocks/getStockByBatch",
  async ({ itemId, batchNumber }, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(
        `stock/batch/${itemId}/${batchNumber}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get all items with their stock (supports server-side search and pagination)
export const getAllItemsWithStock = createAsyncThunk(
  "stocks/getAllItemsWithStock",
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const { search, page, limit, category, includeEmptyBase, stockStatus } = params;
      let url = `stock/items-with-unified-stock`;

      const queryParams = new URLSearchParams();
      if (search) queryParams.append("search", search);
      if (page) queryParams.append("page", page);
      if (limit) queryParams.append("limit", limit);
      if (category) queryParams.append("category", category);
      if (stockStatus) queryParams.append("stockStatus", stockStatus);
      if (includeEmptyBase) queryParams.append("includeEmptyBase", "true");

      const queryString = queryParams.toString();
      if (queryString) url += `?${queryString}`;

      const response = await apiServices.get(url, { signal });
      return response.data;
    } catch (error) {
      if (error.name === 'CanceledError' || error.message === 'canceled') {
        throw error; // Let Redux Toolkit handle the abort automatically
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get lightweight catalog for purchase-items (all items, cascading price/warranty)
export const getPurchaseItemsCatalog = createAsyncThunk(
  "stocks/getPurchaseItemsCatalog",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { search, category } = params;
      let url = `stock/purchase-items-catalog`;

      const queryParams = new URLSearchParams();
      if (search) queryParams.append("search", search);
      if (category) queryParams.append("category", category);

      const queryString = queryParams.toString();
      if (queryString) url += `?${queryString}`;

      const response = await apiServices.get(url, { signal: params.signal });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const checkSerializedStockExist = createAsyncThunk(
  "stocks/checkSerializedStockExist",
  async (serialNumber, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(
        `serializedStock/exist/${serialNumber}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get detailed stock for a specific item with batch info
export const getStockForItem = createAsyncThunk(
  "stocks/getStockForItem",
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`stock/item/${itemId}/stock`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get all stock adjustments (global history)
export const getAllAdjustments = createAsyncThunk(
  "stocks/getAllAdjustments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`stock-ledger/adjustments`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get stock statistics for dashboard KPIs
export const getStockStatistics = createAsyncThunk(
  "stocks/getStockStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`stock/statistics`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Stock Slice
const stockSlice = createSlice({
  name: "stocks",
  initialState: {
    stocks: [],
    pagination: { total: 0, pages: 1, page: 1, limit: 10 },
    statistics: {
      totalItems: 0,
      purchased: 0,
      neverPurchased: 0,
      inStock: 0,
      lowStock: 0,
      outStock: 0,
    },
    stockDetails: {}, // Detailed view of stock for a specific item
    batchDetails: {}, // Batch-specific stock details
    adjustments: [], // Global stock adjustment history
    totalStockValue: 0,
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    purchaseItems: [], // Lightweight catalog for purchase form
    purchaseStatus: "idle",
    error: null,
  },
  reducers: {
    resetStockState: (state) => {
      state.stocks = [];
      state.stockDetails = {};
      state.batchDetails = {};
      state.totalStockValue = 0;
      state.status = "idle";
      state.error = null;
    },
    setStocks: (state, action) => {
      state.stocks = action.payload;
      state.status = "succeeded";
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Stock
      .addCase(createStock.fulfilled, (state, action) => {
        state.stocks.push(action.payload);
      })
      // Fetch Stock by Item
      .addCase(getStockByItem.fulfilled, (state, action) => {
        state.stockDetails[action.meta.arg] = action.payload;
      })
      // Update Stock on Sale
      .addCase(updateStockOnSale.fulfilled, (state, action) => {
        const index = state.stocks.findIndex(
          (stock) => stock._id === action.payload._id
        );
        if (index !== -1) {
          state.stocks[index] = action.payload;
        }
      })
      // Fetch Total Stock Value
      .addCase(getCurrentStockValue.fulfilled, (state, action) => {
        state.totalStockValue = action.payload.totalStockValue;
      })
      // Fetch Stock by Batch
      .addCase(getStockByBatch.fulfilled, (state, action) => {
        state.batchDetails[
          `${action.meta.arg.itemId}_${action.meta.arg.batchNumber}`
        ] = action.payload;
      })
      // Fetch All Items with Stock
      .addCase(getAllItemsWithStock.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllItemsWithStock.fulfilled, (state, action) => {
        state.status = "succeeded";
        const responseData = action.payload.data || action.payload; // Fallback for legacy format
        const pagination = action.payload.pagination || { total: responseData.length, pages: 1, page: 1, limit: responseData.length };

        if (action.meta.arg?.append) {
          state.stocks = [...state.stocks, ...responseData];
        } else {
          state.stocks = responseData;
        }
        state.pagination = pagination;
      })
      .addCase(getAllItemsWithStock.rejected, (state, action) => {
        if (action.meta.aborted) return; // Ignore aborted requests
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch Stock for Specific Item
      .addCase(getStockForItem.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getStockForItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.stockDetails[action.meta.arg] = action.payload;
      })
      .addCase(getStockForItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch All Adjustments
      .addCase(getAllAdjustments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllAdjustments.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.adjustments = action.payload;
      })
      .addCase(getAllAdjustments.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch Stock Statistics
      .addCase(getStockStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload;
      })
      // Fetch Purchase Items Catalog
      .addCase(getPurchaseItemsCatalog.pending, (state) => {
        state.purchaseStatus = "loading";
      })
      .addCase(getPurchaseItemsCatalog.fulfilled, (state, action) => {
        state.purchaseStatus = "succeeded";
        state.purchaseItems = action.payload;
      })
      .addCase(getPurchaseItemsCatalog.rejected, (state, action) => {
        state.purchaseStatus = "failed";
        state.error = action.payload;
      });
  },
});

export const { resetStockState, setStocks } = stockSlice.actions;

export default stockSlice.reducer;

export const getStockValuation = createAsyncThunk(
  "stocks/getStockValuation",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { category, brand, search } = params;
      let url = `stock/valuation`;

      const queryParams = new URLSearchParams();
      if (search) queryParams.append("search", search);
      if (category) queryParams.append("category", category);
      if (brand) queryParams.append("brand", brand);

      const queryString = queryParams.toString();
      if (queryString) url += `?${queryString}`;

      const response = await apiServices.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
